/**
 * Waveform.tsx — live microphone level as a row of bars, the pattern ChatGPT, Gemini and Apple's dictation use to show
 * "I can hear you". Reads an AnalyserNode (Web Audio API) on every animation frame and draws straight to a canvas, so
 * React never re-renders at 60 fps. Without an analyser (the browser refused the audio graph) it shows a calm idle line.
 */
import { useEffect, useRef } from "react";

const BARS = 28;
const BAR_W = 3;
const GAP = 3;
const HEIGHT = 32;
const WIDTH = BARS * BAR_W + (BARS - 1) * GAP;

export function Waveform({ analyser, color = "#ffffff" }: { analyser: AnalyserNode | null; color?: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = WIDTH * dpr;
    el.height = HEIGHT * dpr;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = color;

    const bins = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    // Voice sits low in the spectrum; skip the first bins (rumble) and use the next BARS of them.
    const first = 2;
    const level = new Float32Array(BARS);
    let frame = 0;

    const draw = () => {
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      if (analyser && bins) analyser.getByteFrequencyData(bins);
      for (let i = 0; i < BARS; i++) {
        const raw = bins ? (bins[first + i] ?? 0) / 255 : 0;
        // Rise fast, fall slowly, so the bars feel alive instead of flickering.
        level[i] = raw > level[i]! ? raw : level[i]! * 0.86;
        const h = Math.max(3, Math.min(HEIGHT, 3 + level[i]! * (HEIGHT - 3) * 1.25));
        ctx.beginPath();
        ctx.roundRect(i * (BAR_W + GAP), (HEIGHT - h) / 2, BAR_W, h, BAR_W / 2);
        ctx.fill();
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [analyser, color]);

  return <canvas ref={canvas} style={{ width: WIDTH, height: HEIGHT }} aria-hidden="true" />;
}
